import { useCallback, useMemo, useState } from 'react'
import {
  dataService,
  type TrackingEvent,
  type TrackingSession,
  type CartSession,
  type ShopifyOrder,
} from '../../services/dataService'
import { fmtCurrency, ICON, timeAgo, unwrap } from './knowledgeIndex'
import {
  Button,
  Card,
  Chip,
  ChipGroup,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  LoadMore,
  SearchField,
  Stack,
  Timeline,
} from '../../ds'
import { scopeKey, usePagedRead } from './usePagedRead'
import { humanise } from './ProductKnowledge'

/**
 * Knowledge → Raw events (was "Live feed"; Knowledge v6, MAR-325) — what feeds her memory, and what is coming
 * in now. Ported from the old `screens/Memory.tsx` feed tab.
 *
 * ⚠️ Two v6 elements are DELIBERATELY absent (spec §4, MAR-327): the health headline
 * ("All four sources reporting · last event N seconds ago", "~340 events / hour") and the
 * "what she wrote" column. There is no cross-source endpoint to compute the first and no
 * event→belief join for the second, so either would be a number or a sentence the backend
 * does not hold.
 *
 * MAR-383: the source tiles became one compact strip (a chip per source, `live` or
 * `synced`), and consecutive repeats of the same event fold into one line with ×N
 * (`foldRepeats`) — "Show each" unfolds them. Both use the reads below and nothing else.
 *
 * ⚠️ The source strip carries NO count. The old strip's numbers came from `limit: 1` probes'
 * `pagination.total` — `offset + items.length + (LastEvaluatedKey ? 1 : 0)` — so every
 * non-empty table read "2". The design's "1,208 today" has no backing read; what each
 * source IS, and whether it arrives live, is true and stays.
 *
 * MAR-401 (the canvas's Knowledge-4): this is "Raw events" now — reached only from What she
 * learned, under its own breadcrumb and title (`Knowledge.tsx`). The strip lists the live
 * sources first, then the synced ones. A line carries no source chip (the source is the
 * selected chip above it; a chip per row only repeated it) — only its money or outcome tag.
 * "Show each" sits on the folded line it unfolds, one run at a time, not on the whole stream.
 * ⚠️ Still only CONSECUTIVE repeats fold: an A/B/A/B alternation (connection lost, restored, …)
 * is four lines, because the interleaving is what the merchant is checking — the canvas's
 * "lost, then restored ×38" would merge two different events into one line.
 *
 * ⚠️ The stream is stamped with the (workspace, account, source) it describes and matched
 * at the READ — `usePagedRead`, the same pager the index uses. The old tab held bare
 * `feedRows` and cleared them in an effect, so a workspace switch painted one store's events
 * under the other's name for a frame — and a failed read set `[]`, which then said "No
 * events yet." about a read that never landed.
 *
 * "Filter the stream…" is the old tab's filter, kept with its matching: a case-insensitive
 * substring over who, what, and the row's identifiers (`match` — the text the old screen's
 * raw line carried, so a visitor or order id still finds its row). It filters what is
 * LOADED, and "Nothing matches your filter." is said only when rows are loaded and none
 * match; an empty source still says "No … yet.".
 */

type EventKind = 'view' | 'page' | 'cart' | 'purchase' | 'session'
/** The kind's glyph — kept for whoever renders a kind; the stream's lines no longer chip it. */
export const KIND_ICON: Record<EventKind, string> = {
  view: ICON.view,
  page: ICON.view,
  cart: ICON.cart,
  purchase: ICON.purchase,
  session: ICON.session,
}
/** A row's money or outcome tag, as a chip tone. */
const TAG_TONE: Record<'gold' | 'sage' | 'flat', 'gold' | 'sage' | 'neutral'> = { gold: 'gold', sage: 'sage', flat: 'neutral' }

function eventKind(type: string): EventKind {
  const t = (type || '').toLowerCase()
  if (t.includes('cart')) return 'cart'
  if (/purchase|conversion|paid|\border\b/.test(t) || /checkout.?complet/.test(t)) return 'purchase'
  if (t.includes('session')) return 'session'
  if (t.includes('product')) return 'view'
  return 'page'
}

// The live feed switches between data sources (separate tables), not slices of one stream.
// Each source's rows normalise into a common FeedRow.
export type FeedSource = 'events' | 'sessions' | 'carts' | 'orders'
const FEED_SOURCES: { key: FeedSource; label: string; noun: [string, string] }[] = [
  { key: 'events', label: 'Events', noun: ['event', 'events'] },
  { key: 'sessions', label: 'Sessions', noun: ['session', 'sessions'] },
  { key: 'carts', label: 'Carts', noun: ['cart', 'carts'] },
  { key: 'orders', label: 'Orders', noun: ['order', 'orders'] },
]

/** What she reads from — the live sources first, then the synced ones (MAR-401). `live` is whether the source arrives as it happens. */
export const SOURCE_TILES: { key: string; label: string; live: boolean }[] = [
  { key: 'events', label: 'Events', live: true },
  { key: 'sessions', label: 'Sessions', live: true },
  { key: 'visitors', label: 'Visitors', live: true },
  { key: 'customers', label: 'Customers', live: false },
  { key: 'orders', label: 'Orders', live: false },
  { key: 'products', label: 'Products', live: false },
  { key: 'carts', label: 'Carts', live: false },
]

export interface FeedRow {
  id: string
  ts: number
  kind: EventKind
  who: string
  /**
   * WHO the row is about, by full id — never the display `who`, which truncates ids
   * (`Customer 1487`, `Visitor a1b2c3`) and so can name two people alike. Folding compares
   * this. A row with no id at all gets a key of its own, so it never folds into a stranger.
   */
  whoKey: string
  what: string
  tag?: { text: string; tone: 'gold' | 'sage' | 'flat' }
  /**
   * The identifiers the filter also matches on — the old screen's raw line, never rendered.
   * Without it, a merchant pasting a visitor or order id would find nothing.
   */
  match: string
}

/** The full identity behind a row, for comparison only — never rendered. */
function whoKeyFor(rowId: string, ids: { customer?: unknown; visitor?: unknown; email?: unknown; name?: unknown }) {
  if (ids.customer) return `customer:${String(ids.customer)}`
  if (ids.visitor) return `visitor:${String(ids.visitor)}`
  if (ids.email) return `email:${String(ids.email)}`
  if (ids.name) return `name:${String(ids.name)}`
  return `row:${rowId}`
}

function whoFor(userId?: string, visitorId?: string, name?: string, email?: string) {
  if (name) return name
  if (email) return email
  if (userId) return `Customer ${String(userId).slice(-4)}`
  return `Visitor ${(visitorId || '').slice(-6) || '—'}`
}

/**
 * An event type as words (MAR-412): `capture.trigger_armed` → "Capture trigger armed". There is
 * no label map for tracking types — they are whatever the embed sends — so every one is read the
 * same way; the raw type stays in `match`, so pasting it still finds the row.
 */
export function eventLabel(type: string | null | undefined): string {
  if (!type) return 'Event'
  // Only a lowercase key is rewritten; "VIP" or "iPhone" is already words and keeps its casing.
  return /^[a-z0-9]+(?:[_.-][a-z0-9]+)*$/.test(type) ? humanise(type.replace(/\./g, ' ')) : type
}

export function eventRow(e: TrackingEvent): FeedRow {
  const cartVal = e.properties?.cart_value ?? e.properties?.price
  return {
    id: e.id,
    ts: e.timestamp,
    kind: eventKind(e.type),
    who: whoFor(e.userId, e.visitorId),
    whoKey: whoKeyFor(`event:${e.id}`, { customer: e.userId, visitor: e.visitorId }),
    what: eventLabel(e.type) + (e.properties?.product_name ? ` · ${e.properties.product_name}` : ''),
    tag: cartVal != null ? { text: fmtCurrency(Number(cartVal)), tone: 'gold' } : undefined,
    match: `eventId=${e.id} · type=${e.type} · sessionId=${e.sessionId} · visitorId=${e.visitorId || 'null'} · userId=${e.userId || 'null'} · url=${e.url || '/'}`,
  }
}

export function sessionRow(s: TrackingSession): FeedRow {
  return {
    id: s.sessionId,
    ts: new Date(s.lastUpdate || s.startTime).getTime(),
    kind: 'session',
    who: whoFor(s.userId, s.visitorId),
    whoKey: whoKeyFor(`session:${s.sessionId}`, { customer: s.userId, visitor: s.visitorId }),
    what: `${s.pageViews} page${s.pageViews === 1 ? '' : 's'} · ${s.events} events${s.converted ? ' · converted' : s.bounced ? ' · bounced' : ''}`,
    tag: s.converted ? { text: 'converted', tone: 'sage' } : undefined,
    match: `sessionId=${s.sessionId} · visitorId=${s.visitorId || 'null'} · pageViews=${s.pageViews} · events=${s.events} · converted=${s.converted} · bounced=${s.bounced}`,
  }
}

export function cartRow(c: CartSession): FeedRow {
  return {
    id: c.cartId,
    ts: c.updatedAt || c.createdAt,
    kind: 'cart',
    who: whoFor(c.customerId, c.visitorId, c.customerName, c.customerEmail),
    whoKey: whoKeyFor(`cart:${c.cartId}`, {
      customer: c.customerId,
      visitor: c.visitorId,
      email: c.customerEmail,
      name: c.customerName,
    }),
    what: `${c.status} · ${c.itemCount} item${c.itemCount === 1 ? '' : 's'}${c.recoveryAttempts ? ` · ${c.recoveryAttempts} recovery` : ''}`,
    tag: c.cartValue ? { text: fmtCurrency(c.cartValue), tone: 'gold' } : undefined,
    match: `cartId=${c.cartId} · status=${c.status} · cartValue=${c.cartValue} · itemCount=${c.itemCount} · visitorId=${c.visitorId || 'null'}`,
  }
}

export function orderRow(o: ShopifyOrder): FeedRow {
  // The ShopifyOrder type is snake_case but the API returns camelCase — read both.
  const r = o as unknown as Record<string, unknown>
  const pick = (...keys: string[]) => {
    for (const k of keys) if (r[k] != null) return r[k]
    return undefined
  }
  const id = String(pick('orderId', 'order_id') ?? '')
  const num = pick('orderNumber', 'order_number')
  const total = pick('totalPrice', 'orderTotal', 'total_price')
  const status = (pick('financialStatus', 'financial_status') as string) || ''
  const name = [pick('customerFirstName', 'customer_first_name'), pick('customerLastName', 'customer_last_name')]
    .filter(Boolean)
    .join(' ')
  const email = (pick('email', 'customerEmail') as string) || ''
  const custId = String(pick('customerId', 'customer_id') ?? '')
  const created = pick('createdAt', 'created_at', 'orderCreatedAt') as string | number | undefined
  const totalNum = total != null ? Number(total) : NaN
  const rowId = id || String(num ?? Math.random())
  return {
    id: rowId,
    ts: created ? new Date(created).getTime() : Date.now(),
    kind: 'purchase',
    who: name || email || (custId ? `Customer ${custId.slice(-4)}` : 'Customer'),
    whoKey: whoKeyFor(`order:${rowId}`, { customer: custId, email, name }),
    what: `Order ${num ? `#${num}` : ''}${status ? ` · ${status}` : ''}`.trim(),
    tag: !isNaN(totalNum) ? { text: fmtCurrency(totalNum), tone: 'sage' } : undefined,
    match: `orderId=${id || '—'} · orderNumber=${num ?? '—'} · total=${total ?? '—'} · currency=${(pick('currency') as string) || 'GBP'} · status=${status || '—'}`,
  }
}

/**
 * The old tab's filter, with its semantics: trimmed, case-insensitive, a substring of who,
 * what and the row's identifiers. An empty filter is every row.
 */
export function filterFeed(rows: FeedRow[], filter: string): FeedRow[] {
  const q = filter.trim().toLowerCase()
  if (!q) return rows
  return rows.filter((r) => `${r.who} ${r.what} ${r.match}`.toLowerCase().includes(q))
}

/** A row of the stream as shown: one event, or a run of the same event folded into one. */
export interface FoldedFeedRow extends FeedRow {
  /** How many consecutive rows this line stands for — 1 for an unfolded row. */
  count: number
  /** The oldest row's time in the run (the newest is `ts`, the order the stream gave). */
  firstTs: number
}

/**
 * Two rows are "the same event" when everything the merchant reads on the line matches AND
 * they are about the same person by full id (`whoKey`) — two visitors whose ids end alike
 * read the same on the line and are still two people.
 */
const sameEvent = (a: FeedRow, b: FeedRow) =>
  a.kind === b.kind && a.whoKey === b.whoKey && a.who === b.who && a.what === b.what && (a.tag?.text ?? null) === (b.tag?.text ?? null)

/**
 * Fold CONSECUTIVE repeats of the same event into one line with its count (MAR-383, the
 * canvas's be10). Only runs that are adjacent in the order the stream gave fold — an event
 * that recurs after something else is a new line, because the interleaving is itself what
 * the merchant is reading. Pure; the order is kept.
 */
export function foldRepeats(rows: FeedRow[]): FoldedFeedRow[] {
  const out: FoldedFeedRow[] = []
  for (const r of rows) {
    const last = out[out.length - 1]
    if (last && sameEvent(last, r)) {
      last.count += 1
      if (Number.isFinite(r.ts) && (!Number.isFinite(last.firstTs) || r.ts < last.firstTs)) last.firstTs = r.ts
      if (Number.isFinite(r.ts) && (!Number.isFinite(last.ts) || r.ts > last.ts)) last.ts = r.ts
      continue
    }
    out.push({ ...r, count: 1, firstTs: r.ts })
  }
  return out
}

/** A line of the stream as drawn: a folded run, or one row of a run the merchant unfolded. */
export interface FeedLine extends FoldedFeedRow {
  /** The run this line heads or belongs to — only on runs of more than one. */
  run?: { key: string; size: number; open: boolean; head: boolean }
}

/**
 * The lines to draw (MAR-401): `foldRepeats`, except each run whose key is in `open` is drawn
 * as its rows, one per line. A run's key is the source and its NEWEST row's id, so it survives
 * a load-more that appends older rows. Pure; the order is kept.
 */
export function feedLines(rows: FeedRow[], open: ReadonlySet<string>, source: string): FeedLine[] {
  const out: FeedLine[] = []
  let i = 0
  for (const f of foldRepeats(rows)) {
    const members = rows.slice(i, i + f.count)
    i += f.count
    if (f.count === 1) {
      out.push(f)
      continue
    }
    const key = JSON.stringify([source, members[0].id])
    if (open.has(key)) {
      members.forEach((m, j) => out.push({ ...m, count: 1, firstTs: m.ts, run: { key, size: f.count, open: true, head: j === 0 } }))
    } else {
      out.push({ ...f, run: { key, size: f.count, open: false, head: true } })
    }
  }
  return out
}

/** When a folded line happened: `2d`, or the span of the run (`8d–17d`) when it differs. */
function whenOf(r: FoldedFeedRow): string {
  const newest = timeAgo(r.ts)
  const oldest = timeAgo(r.firstTs)
  return r.count > 1 && oldest !== newest ? `${newest}–${oldest}` : newest
}

export default function LiveFeed({ workspaceId, accountId }: { workspaceId?: string; accountId?: string }) {
  const [source, setSource] = useState<FeedSource>('events')
  const [filter, setFilter] = useState('')

  // Each feed source is a separate table with its own query. ⚠️ `hasMore` is read off the
  // CURSOR alone: a "there is more" with no cursor is a button that cannot do anything.
  const fetchPage = useCallback(
    async(cursor: unknown): Promise<{ items: FeedRow[]; cursor: unknown }> => {
      const params = { workspaceId: workspaceId || '', accountId, limit: 40, lastEvaluatedKey: cursor ?? undefined }
      if (source === 'sessions') {
        const r = unwrap<TrackingSession>(await dataService.queryTrackingSessions(params))
        return { items: r.items.map(sessionRow), cursor: r.lastKey ?? null }
      }
      if (source === 'carts') {
        const r = unwrap<CartSession>(await dataService.queryCartSessions(params))
        return { items: r.items.map(cartRow), cursor: r.lastKey ?? null }
      }
      if (source === 'orders') {
        const r = unwrap<ShopifyOrder>(await dataService.queryShopifyOrders(params))
        return { items: r.items.map(orderRow), cursor: r.lastKey ?? null }
      }
      const r = unwrap<TrackingEvent>(await dataService.queryTrackingEvents(params))
      return { items: r.items.map(eventRow), cursor: r.lastKey ?? null }
    },
    [source, workspaceId, accountId]
  )
  // ⚠️ Every scope dimension is in the key: a stream for another workspace, account or
  // source is not current, and is never returned.
  const key = workspaceId ? scopeKey('feed', source, workspaceId, accountId) : null
  const { page: current, loadMore, retry } = usePagedRead<FeedRow>(key, fetchPage)
  const rows = current?.items
  const shown = useMemo(() => filterFeed(rows ?? [], filter), [rows, filter])
  /** Runs the merchant unfolded with their own "Show each" — per line, never the whole stream. */
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(() => new Set())
  const lines = useMemo(() => feedLines(shown, unfolded, source), [shown, unfolded, source])
  const folded = lines.some((l) => l.count > 1)
  const toggleRun = (key: string) =>
    setUnfolded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const noun = FEED_SOURCES.find((s) => s.key === source)?.noun ?? ['event', 'events']
  const sourceLabel = FEED_SOURCES.find((s) => s.key === source)?.label ?? 'Events'

  const loadedNoun = (n: number) => (n === 1 ? noun[0] : noun[1])

  return (
    <Stack data-testid="kn-feed">
      <ChipGroup label="What she reads from">
        {SOURCE_TILES.map((t) => (
          <Chip
            key={t.key}
            tone={t.live ? 'sage' : 'neutral'}
            dot={t.live}
            data-testid="kn-src-tile"
            data-live={t.live ? 'true' : 'false'}
          >
            {t.label} · {t.live ? 'live' : 'synced'}
          </Chip>
        ))}
      </ChipGroup>

      <Card data-testid="kn-feed-stream">
        <Stack>
          <FilterBar>
            {/* All four, always. An empty source says so itself in one line below. */}
            <ChipGroup label="Source">
              {FEED_SOURCES.map((s) => (
                <Chip
                  kind="select"
                  key={s.key}
                  data-feed-source={s.key}
                  selected={source === s.key}
                  onToggle={() => setSource(s.key)}
                >
                  {s.label}
                </Chip>
              ))}
            </ChipGroup>
            <SearchField value={filter} onChange={setFilter} placeholder="Filter the stream…" label="Filter the stream" />
          </FilterBar>

        {!current ? (
          <LoadingState caption="Loading…" />
        ) : current.failed ? (
          <ErrorState
            title={`Could not load your ${noun[1]} just now, so nothing is shown rather than a stream that looks quiet.`}
            onRetry={retry}
          />
        ) : (
          <>
            {shown.length > 0 && (
              <Timeline
                label={`${sourceLabel} coming in`}
                items={lines.map((r) => ({
                  when: whenOf(r),
                  datetime: Number.isFinite(r.ts) ? new Date(r.ts).toISOString() : undefined,
                  title: (
                    <span data-testid="kn-ev" data-kind={r.kind}>
                      <span>{r.what}</span> · <span data-testid="kn-ev-who">{r.who}</span>
                      {r.count > 1 && (
                        <>
                          {' '}
                          <span data-testid="kn-ev-count">×{r.count}</span>
                        </>
                      )}
                      {r.run?.head && (
                        <>
                          {/* A separator, not a bare space: the link read as part of "×19" (MAR-412). */}
                          {' · '}
                          {/* The run's own control: it unfolds (or folds back) this line alone. */}
                          <Button
                            variant="link"
                            data-testid={r.run.open ? 'kn-ev-fold' : 'kn-ev-each'}
                            aria-expanded={r.run.open}
                            onClick={() => r.run && toggleRun(r.run.key)}
                          >
                            {r.run.open ? `Fold these ${r.run.size}` : 'Show each'}
                          </Button>
                        </>
                      )}
                    </span>
                  ),
                  // No source chip: the source is the selected chip above. A money or
                  // outcome tag is the row's own fact, and stays.
                  detail: r.tag ? <Chip tone={TAG_TONE[r.tag.tone]}>{r.tag.text}</Chip> : undefined,
                }))}
              />
            )}
            {current.items.length === 0 ? (
              <EmptyState size="sm">No {noun[1]} yet.</EmptyState>
            ) : (
              shown.length === 0 && <EmptyState size="sm">Nothing matches your filter.</EmptyState>
            )}
            <LoadMore
              hasMore={current.hasMore}
              onLoad={loadMore}
              label="Load more"
              state={current.loadingMore ? 'loading' : current.moreFailed ? 'error' : 'idle'}
              note={
                current.moreFailed
                  ? 'That did not load, so nothing was added. What is above is unaffected — try again.'
                  : undefined
              }
              shown={
                current.items.length > 0
                  ? // "All" only when the server returned no cursor — the same rule as search.
                    current.hasMore
                    ? `Showing the ${current.items.length} ${loadedNoun(current.items.length)} loaded so far — there are more.${folded ? ' Repeats in a row are folded into one line.' : ''}`
                    : `Showing all ${current.items.length} ${loadedNoun(current.items.length)}.${folded ? ' Repeats in a row are folded into one line.' : ''}`
                  : undefined
              }
            />
          </>
        )}
        </Stack>
      </Card>
    </Stack>
  )
}
